// NuVe Boost — Twilio error-code → plain-English messages.
//
// The backend stores the raw Twilio error code + message on each failed
// BoostRecipient. Twilio's own `errorMessage` is often terse or absent
// (a status callback frequently carries only an ErrorCode like "30005"),
// which left the Delivery Report showing an unhelpful "Error 30005".
//
// This map turns the common messaging error codes into a short reason an
// employee can read and act on ("not a real/active mobile number", "the
// customer replied STOP", etc.). `friendlyTwilioError()` prefers this map,
// then falls back to Twilio's own message, then to a bare code.
//
// Codes: https://www.twilio.com/docs/api/errors  (messaging 30xxx / 21xxx)

export const TWILIO_ERRORS = {
  // ── 30xxx: carrier / handset delivery failures ──
  30001: 'Sending queue is backed up. Try again in a few minutes.',
  30002: 'The messaging account is suspended. Contact support.',
  30003:
    'The phone is unreachable — it may be off, out of coverage, or the number is no longer active.',
  30004:
    'The carrier or handset blocked this message. The customer may have blocked texts from this number.',
  // Deliberately hedged. Twilio returns 30005 both for a number that truly
  // doesn't exist and for a carrier or handset refusing this particular sender,
  // so staff must not read it as proof the customer gave a fake number.
  30005:
    "The carrier couldn't reach that handset — usually a disconnected or wrong number, but it can also happen to a number that works.",
  30006:
    "That's a landline or an unreachable carrier — it can't receive texts.",
  30007:
    'The carrier filtered this message as spam. Try rewording it or removing links.',
  30008: "Delivery failed for an unknown reason on the carrier's side.",
  30009: 'A part of the message was lost in transit. Try resending.',
  30010: 'Message price exceeded the allowed maximum.',
  30011: "The receiving phone doesn't support picture (MMS) messages.",
  30017:
    'The carrier network is congested or having an outage. Try again shortly.',
  30019:
    'The picture/message is too large for the carrier. Use a smaller photo or shorter text.',
  30022:
    'Daily send limit for this number was reached (carrier rate limit). Try again tomorrow.',
  30023: 'Daily message cap for this number was reached. Try again tomorrow.',
  30024: 'The sending number is not set up for this destination.',
  30026: 'Message blocked — the destination country or number is not allowed.',
  30032:
    'The sending number is not yet verified with carriers. Contact support.',
  30034:
    'The sending number is not registered for business texting yet. Contact support.',
  30036: 'The message expired before it could be delivered. Try resending.',

  // ── 21xxx: request-level / number-validity rejections ──
  21211:
    "That phone number isn't valid. Check the digits on the customer's profile.",
  21214: "That phone number isn't reachable and can't receive texts.",
  21408:
    'Texting to this region is not enabled on the account. Contact support.',
  21606: 'The sending number can no longer send texts. Contact support.',
  21610:
    'The customer replied STOP and is unsubscribed. They must text START to opt back in.',
  21611: 'The sending number is overloaded. Try again in a few minutes.',
  21612: "This number can't be reached over the current route.",
  21614: "That number isn't a valid mobile number — it can't receive texts.",

  // ── our synthetic codes ──
  INVALID_NUMBER:
    "Skipped — this number is on the Do Not Send list. Update the customer's phone number and verify it.",
  // never reached Twilio; auto-refunded
  TRANSPORT:
    "Couldn't reach the SMS service — the message was not sent and the credit was refunded. Try again.",
};

/**
 * Resolve a failed recipient's error to an employee-friendly line.
 *
 * @param {string|number} code  Twilio error code (r.errorCode)
 * @param {string} [rawMessage] Twilio's own message (r.errorMessage)
 * @returns {string} A readable reason, always non-empty when either input is.
 */
export function friendlyTwilioError(code, rawMessage) {
  const key = code != null ? String(code).trim() : '';
  if (key && TWILIO_ERRORS[key]) return TWILIO_ERRORS[key];
  if (rawMessage) return rawMessage;
  if (key) return `Delivery failed (error ${key}).`;
  return 'Delivery failed.';
}
